import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";
import { ActionIcon, Group, Switch, Text, TextInput, Tooltip } from "@mantine/core";
import { useDocumentTitle } from "@mantine/hooks";
import {
  IconBabyCarriage,
  IconBolt,
  IconBriefcase,
  IconHome,
  IconRefresh,
  IconSearch,
  IconStethoscope,
  IconX,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/react/shallow";

import { WithPatientNavbar } from "@layouts/WithPatientNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { TabsContent, TabsList, TabsRoot } from "@components/Tabs";

import { PAGE_TITLE } from "@constants/index";

import { consultationQKeys } from "@hooks/queries/consultation/queryKeys";
import { useAppStore } from "@hooks/store/useAppStore";
import { useRefreshAnimation } from "@hooks/useRefreshAnimation";

import { ConsultationsTable } from "./components/ConsultationsTable";
// import { FastConsultation } from "./components/FastConsultation";
import { StyledConsultation } from "./Consultation.styled";


export const Consultation = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { patientId } = useParams();

  useDocumentTitle(`${t("consultation")} • ${PAGE_TITLE}`);
  const [storeTabs, setStoreTabs] = useAppStore(
    useShallow(state => [state.tabs, state.setTabs])
  );
  const { state } = useLocation();

  const [currentTab, setCurrentTab] = useState(storeTabs.consultationTab);
  const [searchFilter, setSearchFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { buttonRef } = useRefreshAnimation({ isRefreshing });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    //? Replace with query param?
    if (state?.isFinalizedConsultation) {
      setCurrentTab("all");
      setStoreTabs({ consultationTab: "all" });
    }
  }, [state]);

  useEffect(() => {
    if (currentTab) setCurrentTab(currentTab);
  }, []);

  return (
    <WithSidebar hasPatientNavbar>
      <WithPatientNavbar>
        <StyledConsultation>
          <TabsRoot
            value={currentTab as string}
            onValueChange={val => {
              setCurrentTab(val);
              setStoreTabs({ consultationTab: val });
            }}>
            <div className="header">
              <Group gap={"md"}>
                <TabsList
                  comboboxItems={[
                    { label: t("new-consultation"), value: "new" },
                    {
                      label: t("all-consultations"),
                      value: "all",
                    },
                  ]}
                />
                {currentTab === "all" && (
                  <Tooltip
                    label={t("refresh-data")}
                    position="right-end"
                    fz={12}
                    events={{ focus: true, hover: true, touch: true }}>
                    <ActionIcon
                      ref={buttonRef}
                      variant={"default"}
                      size={"md"}
                      onClick={() => {
                        queryClient.invalidateQueries(
                          consultationQKeys.patient(patientId as string)
                        );
                      }}>
                      <IconRefresh
                        className={"refresh-icon"}
                        color="var(--mantine-color-black)"
                        size={16}
                      />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
              {currentTab === "all" && (
                <Group gap="md">
                  <Switch
                    label={t("include-archived")}
                    checked={showArchived}
                    onChange={event => {
                      setShowArchived(event.currentTarget.checked);
                      setCurrentPage(1);
                    }}
                  />
                  <TextInput
                    placeholder={t("search-for-record")}
                    w={{ base: 300, md: 350, lg: 400 }}
                    value={searchFilter}
                    onChange={e => {
                      setCurrentPage(1);
                      setSearchFilter(e.currentTarget.value.toLocaleUpperCase());
                    }}
                    leftSection={<IconSearch size={16} />}
                    rightSection={
                      searchFilter && (
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => setSearchFilter("")}>
                          <IconX size={16} />
                        </ActionIcon>
                      )
                    }
                  />
                </Group>
              )}
            </div>

            <TabsContent value="new" className="new-consultation-wrapper">
              <div className="instructions-header">
                <h2>{t("create-consultation")}</h2>
                <Text c="dimmed" fz={"sm"} mt={"xs"}>
                  {t("create-consultation-description")}
                </Text>
              </div>

              <div className="consultation-cards">
                {/* <FastConsultation
                  opened={modalOpened}
                  close={modalClose}
                  setCurrentParentTab={setCurrentTab}
                /> */}
                <Link
                  to="#"
                  // onClick={modalOpen} // Disabled because of misuse by doctors
                  className="consultation-cards-item disabled">
                  <div className="icon-wrapper yellow">
                    <IconBolt size={"2rem"} stroke={1.5} />
                  </div>
                  <p>{t("fast")}</p>
                  <p>{t("fast-consultation-description")}</p>
                </Link>

                <Link to="general" className="consultation-cards-item">
                  <div className="icon-wrapper purple">
                    <IconStethoscope size={"2rem"} stroke={1.5} />
                  </div>
                  <p>{t("general")}</p>
                  <p>{t("general-consultation-description")}</p>
                </Link>
                <Link to="#" className="consultation-cards-item disabled">
                  <div className="icon-wrapper blue">
                    <IconBriefcase size={"2rem"} stroke={1.5} />
                  </div>
                  <p>{t("occupational")}</p>
                  <p>{t("occupational-consultation-description")}</p>
                </Link>
                <Link to="#" className="consultation-cards-item disabled">
                  <div className="icon-wrapper green">
                    <IconHome size={"2rem"} stroke={1.5} />
                  </div>
                  <p>{t("home")}</p>
                  <p>{t("home-consultation-description")}</p>
                </Link>
                <Link to="#" className="consultation-cards-item disabled">
                  <div className="icon-wrapper pink">
                    <IconBabyCarriage size={"2rem"} stroke={1.5} />
                  </div>
                  <p>{t("paediatric")}</p>
                  <p>{t("paediatric-consultation-description")}</p>
                </Link>
              </div>
            </TabsContent>

            <TabsContent value="all">
              <ConsultationsTable
                setIsRefreshing={setIsRefreshing}
                searchFilter={searchFilter}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                includeArchived={showArchived}
              />
            </TabsContent>
          </TabsRoot>
        </StyledConsultation>
      </WithPatientNavbar>
    </WithSidebar>
  );
};
